<script setup lang="ts">
/**
 * Метки задачи — макет 13:490: чипы под подписью «МЕТКИ», выбор из меток
 * проекта и заведение новой на лету.
 *
 * Заводить метку прямо здесь важнее, чем кажется: иначе, чтобы отметить задачу
 * словом «backend», нужно уйти в настройки проекта, вернуться и вспомнить,
 * зачем уходил. Цвет новой метки берём по кругу из палитры настроек — выбор
 * цвета в этот момент только мешает.
 */
import { onClickOutside, useEventListener } from '@vueuse/core'
import { Plus, X } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { COLUMN_COLORS } from '@/features/settings/api'

import { useIssueStore } from '../store'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t } = useI18n()
const store = useIssueStore()

const root = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const query = ref('')

onClickOutside(root, () => {
  isOpen.value = false
})

useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape') isOpen.value = false
})

const chosen = computed(() => (store.issue?.labels ?? []).map((label) => label.id))

const found = computed(() => {
  const needle = query.value.trim().toLowerCase()

  return store.labels.filter((label) => !needle || label.name.toLowerCase().includes(needle))
})

/** Точного совпадения нет — значит набранное имя ещё свободно */
const isNew = computed(
  () =>
    query.value.trim().length > 0 &&
    !store.labels.some((label) => label.name.toLowerCase() === query.value.trim().toLowerCase()),
)

const guard = (run: () => Promise<void>) => {
  run().catch((failure: unknown) => emit('failed', failure))
}

const toggle = (id: string) => {
  const next = chosen.value.includes(id)
    ? chosen.value.filter((entry) => entry !== id)
    : [...chosen.value, id]

  guard(() => store.setIssueLabels(next))
}

const create = () => {
  const name = query.value.trim()

  if (!name) return

  const color = COLUMN_COLORS[store.labels.length % COLUMN_COLORS.length]!

  query.value = ''
  isOpen.value = false

  guard(() => store.addLabel(name, color))
}
</script>

<template>
  <div ref="root" class="lpick">
    <span class="lpick__chips">
      <span
        v-for="label in store.issue?.labels ?? []"
        :key="label.id"
        class="lchip"
        :style="{ borderColor: label.color, color: label.color }"
      >
        {{ label.name }}
        <button
          type="button"
          class="lchip__off"
          :aria-label="t('issue.labels.remove', { name: label.name })"
          @click="toggle(label.id)"
        >
          <X :size="10" />
        </button>
      </span>

      <button
        type="button"
        class="lpick__add"
        :aria-label="t('issue.labels.add')"
        @click="isOpen = !isOpen"
      >
        <Plus :size="12" />
      </button>
    </span>

    <div v-if="isOpen" class="lpick__panel">
      <input
        v-model="query"
        class="lpick__search"
        type="text"
        :placeholder="t('issue.labels.search')"
        :aria-label="t('issue.labels.search')"
        @keydown.enter="isNew && create()"
      />

      <button
        v-for="label in found"
        :key="label.id"
        type="button"
        class="lpick__option"
        :aria-selected="chosen.includes(label.id)"
        role="option"
        @click="toggle(label.id)"
      >
        <span class="lpick__dot" :style="{ background: label.color }" />
        {{ label.name }}
        <span v-if="chosen.includes(label.id)" class="lpick__on">✓</span>
      </button>

      <button v-if="isNew" type="button" class="lpick__option lpick__option--new" @click="create">
        <Plus :size="12" />
        {{ t('issue.labels.create', { name: query.trim() }) }}
      </button>

      <p v-else-if="!found.length" class="lpick__empty">{{ t('issue.labels.none') }}</p>
    </div>
  </div>
</template>

<style scoped>
.lpick {
  position: relative;
}

.lpick__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

/* Чип метки — 19 в высоту, как в макете: цвет живёт в рамке и тексте, заливка
   осталась бы пятном в колонке, где всё остальное — текст. */
.lchip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 19px;
  padding: 0 8px;
  border: 1px solid;
  border-radius: var(--radius-full);
  font-size: var(--font-size-overline);
  line-height: 13px;
}

.lchip__off {
  display: inline-flex;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.6;
}

.lchip__off:hover {
  opacity: 1;
}

.lpick__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  padding: 0;
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-full);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.lpick__add:hover {
  color: var(--color-text-primary);
}

.lpick__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 220px;
  max-height: 280px;
  padding: 6px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.lpick__search {
  height: 28px;
  margin-bottom: 4px;
  padding: 0 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
}

.lpick__option {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 28px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.lpick__option:hover {
  background: var(--color-bg-muted);
}

.lpick__option--new {
  color: var(--color-primary);
}

.lpick__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.lpick__on {
  margin-left: auto;
  color: var(--color-primary);
}

.lpick__empty {
  margin: 0;
  padding: 8px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
